import { Injectable } from '@angular/core';
import {
  Lead,
  ModeOfTransport,
  RoutingAnswer,
  RoutingAnswerElement,
  TimePoint,
} from '../lead.model';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { LeadService } from '../services/leads/lead.service';

export interface RouteElement {
  route: number[][];
  targetLead: Lead;
  arrival: TimePoint;
  startExamination: TimePoint;
  departure: TimePoint;
}

//this represents the map, when there is no route calculated
export interface LeadInformation {
  type: 'leadInformation';
  leads: Lead[];
}

//this represents the map, when there is a route calculated
export interface RouteInformation {
  type: 'routeInformation';
  unreachableLeads: Lead[];
  start: TimePoint;
  routeElements: RouteElement[];
  modeOfTransport: ModeOfTransport;
  overheadPerLeadInMinutes: number;
  maximumTourLengthInHours: number;
  timePerLeadInMinutes: number;
  withOpeningTimes: boolean;
}

export type MapInformation = LeadInformation | RouteInformation;

@Injectable()
export class RouteInformationService {
  private readonly currentLead = new BehaviorSubject<Lead | undefined>(
    undefined,
  );
  public readonly currentLead$ = this.currentLead.asObservable();

  private readonly redrawRoute = new ReplaySubject<void>(1);
  public readonly redrawRoute$ = this.redrawRoute.asObservable();

  private mapInformation: MapInformation;

  constructor(leadService: LeadService) {
    this.mapInformation = {
      type: 'leadInformation',
      leads: leadService.getSelectedLeads(),
    };
    this.redrawRoute.next();
  }

  public getMapInformation() {
    return this.mapInformation;
  }

  public getCurrentLead() {
    return this.currentLead.value;
  }

  public setRoutingResponse(
    routingAnswer: RoutingAnswer,
    selectedLeads: Lead[],
    withOpeningTimes: boolean,
  ) {
    const order = routingAnswer.routes.map(
      (routingAnswerElement: RoutingAnswerElement) =>
        routingAnswerElement.orderTarget,
    );
    const unreachableLeads = Array.from(
      { length: selectedLeads.length },
      (_, i) => i,
    )
      .filter((i) => !order.includes(i))
      .map((i: number) => selectedLeads[i]);
    this.mapInformation = {
      type: 'routeInformation',
      start: routingAnswer.now,
      maximumTourLengthInHours: routingAnswer.maximumTourLengthInHours,
      overheadPerLeadInMinutes: routingAnswer.overheadPerLeadInMinutes,
      timePerLeadInMinutes: routingAnswer.timePerLeadInMinutes,
      unreachableLeads: unreachableLeads,
      modeOfTransport: routingAnswer.modeOfTransport,
      withOpeningTimes: withOpeningTimes,
      routeElements: routingAnswer.routes.map(
        (routingAnswerElement: RoutingAnswerElement) => {
          const routeElement: RouteElement = {
            route: routingAnswerElement.route.map((coordinate) => [
              coordinate.longitude,
              coordinate.latitude,
            ]),
            arrival: routingAnswerElement.arrival,
            departure: routingAnswerElement.departure,
            startExamination: routingAnswerElement.startExamination,
            targetLead: selectedLeads[routingAnswerElement.orderTarget],
          };
          return routeElement;
        },
      ),
    };
    this.setCurrentLead(undefined);
  }

  public updateTour(removedLead: Lead) {
    if (this.mapInformation.type === 'leadInformation') {
      this.mapInformation.leads = this.mapInformation.leads.filter(
        (lead: Lead) => lead.id !== removedLead.id,
      );
    } else if (this.mapInformation.type === 'routeInformation') {
      //first check if removedLead is the first lead in tour
      if (
        this.mapInformation.routeElements[0].targetLead.id === removedLead.id
      ) {
        this.mapInformation.start =
          this.mapInformation.routeElements[0].departure;
        this.mapInformation.routeElements =
          this.mapInformation.routeElements.slice(1);
      } else {
        this.mapInformation = {
          type: 'leadInformation',
          leads: this.mapInformation.routeElements
            .map((routeElement) => routeElement.targetLead)
            .concat(this.mapInformation.unreachableLeads)
            .filter((lead) => lead.id != removedLead.id),
        };
      }
    }

    this.redrawRoute.next();
  }

  public setCurrentLead(currentLead: Lead | undefined) {
    this.currentLead.next(currentLead);
    this.redrawRoute.next();
  }
}
